/**
 * Conversation list item component.
 *
 * Displays a single conversation in the conversation list. Shows partner name,
 * last message preview, unread count, and provides actions for marking as
 * read/unread and deleting conversations.
 *
 * @module ConversationListItem
 */

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { Conversation } from "@/types/partner-finder";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface ConversationListItemProps {
  readonly conversation: Conversation;
  readonly isSelected: boolean;
  readonly onClick: () => void;
  readonly onMarkUnread?: (conversationId: string) => void;
  readonly onDelete?: (conversationId: string) => void;
}

/**
 * Component for displaying a single conversation in the conversation list
 */
export function ConversationListItem({
  conversation,
  isSelected,
  onClick,
  onMarkUnread,
  onDelete,
}: ConversationListItemProps) {
  const { t } = useTranslation();
  const handleMarkUnread = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkUnread?.(conversation.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      onDelete &&
      globalThis.confirm(
        t("partnerFinder.dialogs.deleteConversation.confirmForUser", {
          name: conversation.partnerName,
          defaultValue: `Are you sure you want to delete the conversation with ${conversation.partnerName}?`,
        }),
      )
    ) {
      onDelete(conversation.id);
    }
  };

  let truncatedMessage = t("partnerFinder.messages.noMessagesYet", {
    defaultValue: "No messages yet",
  });
  if (conversation.lastMessage) {
    const words = conversation.lastMessage.split(" ");
    const truncated = words.slice(0, 8).join(" ");
    truncatedMessage = words.length > 8 ? `${truncated}...` : truncated;
  }

  return (
    <div
      className={cn(
        "w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors border-b relative group",
        isSelected && "bg-muted",
      )}
    >
      <button
        onClick={onClick}
        className="flex items-center gap-3 flex-1 min-w-0 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-semibold truncate flex-1 min-w-0">
              {conversation.partnerName}
            </span>
            {conversation.lastMessageTime && (
              <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                {conversation.lastMessageTime}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-xs text-muted-foreground truncate flex-1 min-w-0">
              {truncatedMessage}
            </p>
            {conversation.unreadCount > 0 && (
              <Badge variant="default" className="shrink-0">
                {conversation.unreadCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs">
              {conversation.partnerLevel}
            </Badge>
            {conversation.sessionInfo && (
              <span
                className={`text-xs ${conversation.sessionInfo.remaining === 0 ? "text-destructive" : "text-muted-foreground"}`}
              >
                {t("partnerFinder.messages.sessionProgress", {
                  sent: conversation.sessionInfo.sent,
                  remaining: conversation.sessionInfo.remaining,
                  defaultValue: "{{sent}} sent · {{remaining}} left",
                })}
              </span>
            )}
          </div>
        </div>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0 opacity-100 transition-opacity lg:h-8 lg:w-8 lg:opacity-0 lg:group-hover:opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleMarkUnread}>
            {conversation.unreadCount > 0
              ? t("partnerFinder.buttons.markAsRead", {
                  defaultValue: "Mark as read",
                })
              : t("partnerFinder.buttons.markAsUnread", {
                  defaultValue: "Mark as unread",
                })}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleDelete}
            className="text-destructive focus:text-destructive"
          >
            {t("partnerFinder.buttons.delete", { defaultValue: "Delete" })}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
